import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../auth/AuthProvider";
import Workout from "../models/Workout";
import GlobalWorkout from "../models/GlobalWorkout";
import StarredWorkout from "../models/StarredWorkout";

const HomePage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [recentWorkouts, setRecentWorkouts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user) {
      loadRecentWorkouts();
    }
  }, [user]);

  const loadRecentWorkouts = async () => {
    try {
      // Get user workouts and starred global workouts
      const [userWorkouts, starredIds, globalWorkouts] = await Promise.all([
        Workout.getAll(user.uid),
        StarredWorkout.getStarredIds(user.uid),
        GlobalWorkout.getAll()
      ]);

      // Get starred globals
      const starredGlobals = globalWorkouts.filter(w => starredIds.has(w.id));

      // Combine and sort by lastPerformedAt
      const allWorkouts = [
        ...userWorkouts.map(w => ({ ...w, isGlobal: false })),
        ...starredGlobals
      ];

      // Sort by lastPerformedAt (most recent first) and take top 3
      const sorted = allWorkouts
        .filter(w => !w.isGlobal) // Only show user workouts in recent (they have lastPerformedAt)
        .sort((a, b) => {
          if (!a.lastPerformedAt) return 1;
          if (!b.lastPerformedAt) return -1;
          const aTime = a.lastPerformedAt.toDate ? a.lastPerformedAt.toDate() : new Date(a.lastPerformedAt);
          const bTime = b.lastPerformedAt.toDate ? b.lastPerformedAt.toDate() : new Date(b.lastPerformedAt);
          return bTime - aTime;
        })
        .slice(0, 3);

      // If no recent workouts, show starred globals
      if (sorted.length === 0 && starredGlobals.length > 0) {
        setRecentWorkouts(starredGlobals.slice(0, 3));
      } else if (sorted.length === 0) {
        // Show top 3 global workouts by popularity
        const topGlobal = globalWorkouts
          .sort((a, b) => (b.popularity || 0) - (a.popularity || 0))
          .slice(0, 3);
        setRecentWorkouts(topGlobal);
      } else {
        setRecentWorkouts(sorted);
      }
    } catch (err) {
      console.error("Error loading workouts:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleWorkoutClick = (workoutId) => {
    navigate(`/workout/${workoutId}`);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-gray-500 dark:text-gray-400">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-2">
            BEDRELY
          </h1>
          <p className="text-lg text-gray-600 dark:text-gray-400">
            Ready to get started?
          </p>
        </div>

        {/* Main Action Buttons */}
        <div className="space-y-4 mb-12">
          {/* Start Workout Button */}
          <button
            onClick={() => navigate("/workouts")}
            className="w-full bg-indigo-600 hover:bg-indigo-700 text-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-200 transform hover:scale-[1.02] active:scale-[0.98]"
          >
            <div className="flex items-center justify-center gap-4">
              <svg className="w-12 h-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
              <div className="text-left">
                <div className="text-2xl font-bold">START WORKOUT</div>
                <div className="text-sm text-indigo-200">Choose from library or create your own</div>
              </div>
            </div>
          </button>

          {/* Track Habit Button */}
          <button
            onClick={() => navigate("/challenges")}
            className="w-full bg-green-600 hover:bg-green-700 text-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-200 transform hover:scale-[1.02] active:scale-[0.98]"
          >
            <div className="flex items-center justify-center gap-4">
              <svg className="w-12 h-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div className="text-left">
                <div className="text-2xl font-bold">TRACK HABIT</div>
                <div className="text-sm text-green-200">Log your daily progress</div>
              </div>
            </div>
          </button>
        </div>

        {/* Quick Access: Recent/Suggested Workouts */}
        {recentWorkouts.length > 0 && (
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md p-6">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              Quick Access
            </h2>
            <div className="space-y-3">
              {recentWorkouts.map((workout) => (
                <button
                  key={workout.id}
                  onClick={() => handleWorkoutClick(workout.id)}
                  className="w-full text-left p-4 bg-gray-50 dark:bg-gray-700 hover:bg-gray-100 dark:hover:bg-gray-600 rounded-lg transition-colors"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-medium text-gray-900 dark:text-white truncate">
                          {workout.name}
                        </span>
                        {workout.isGlobal && (
                          <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200 flex-shrink-0">
                            Global
                          </span>
                        )}
                        {workout.category && (
                          <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-gray-200 text-gray-700 dark:bg-gray-600 dark:text-gray-300 flex-shrink-0">
                            {workout.category}
                          </span>
                        )}
                      </div>
                      {workout.description && (
                        <p className="text-sm text-gray-600 dark:text-gray-400 truncate">
                          {workout.description}
                        </p>
                      )}
                    </div>
                    <svg className="w-6 h-6 text-gray-400 flex-shrink-0 ml-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </div>
                </button>
              ))}
            </div>

            {/* Browse All Link */}
            <button
              onClick={() => navigate("/workouts")}
              className="w-full mt-4 text-center text-sm text-indigo-600 dark:text-indigo-400 hover:text-indigo-700 dark:hover:text-indigo-300 font-medium"
            >
              Browse all workouts →
            </button>
          </div>
        )}

        {/* Secondary Actions */}
        <div className="mt-8 grid grid-cols-2 gap-4">
          <button
            onClick={() => navigate("/goals")}
            className="p-4 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-xl shadow-md transition-colors"
          >
            <div className="text-center">
              <svg className="w-8 h-8 mx-auto mb-2 text-purple-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
              <div className="text-sm font-medium text-gray-900 dark:text-white">Goals</div>
            </div>
          </button>

          <button
            onClick={() => navigate("/profile")}
            className="p-4 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-xl shadow-md transition-colors"
          >
            <div className="text-center">
              <svg className="w-8 h-8 mx-auto mb-2 text-gray-600 dark:text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
              <div className="text-sm font-medium text-gray-900 dark:text-white">Profile</div>
            </div>
          </button>
        </div>
      </div>
    </div>
  );
};

export default HomePage;
